import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TableLink, TablePagination } from '@/components/global';
import { entityLinkClass } from '@/lib/entityLink';
import { machineDetailPath } from '@/lib/entityPaths';
import {
  AlertCircle,
  ChevronRight,
  ClipboardList,
  FilterX,
  Monitor,
  Plus,
  Search,
  X,
} from 'lucide-react';
import {
  MachineLifecycleBadge,
  MachineLifecycleStatus,
  RAGBadge,
  RAGStatus,
} from '../../components/machines';
import { RAGSummary } from '../../components/machines/RAGSummaryCards';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { apiFetch } from '../../lib/api';
import { cn } from '../../lib/utils';

const RAG_STATUS_VALUES: readonly string[] = ['GREEN', 'AMBER', 'RED', 'OFFLINE'];
const LIFECYCLE_STATUS_VALUES: readonly MachineLifecycleStatus[] = [
  'DRAFT',
  'SCHEDULED',
  'LIVE',
  'ARCHIVED',
  'UNMANAGED',
];

type RagStatusFilter = 'GREEN' | 'AMBER' | 'RED' | 'OFFLINE' | null;
type LifecycleFilter = MachineLifecycleStatus | null;

const PAGE_SIZE = 50;

function parseRagStatusFromParams(value: string | null): RagStatusFilter {
  if (!value || !RAG_STATUS_VALUES.includes(value)) return null;
  return value as RagStatusFilter;
}

function parseLifecycleFromParams(value: string | null): LifecycleFilter {
  if (!value || !LIFECYCLE_STATUS_VALUES.includes(value as MachineLifecycleStatus)) return null;
  return value as LifecycleFilter;
}

interface MachineData {
  machine: {
    id: string;
    name: string;
    lifecycleStatus: MachineLifecycleStatus;
    venue?: { name: string };
  };
  ragStatus: { status: RAGStatus; percentageFilled: number };
  isOnline: boolean;
}

interface MachineListResponse {
  items: MachineData[];
  total: number;
  page: number;
  pageSize: number;
  summary: RAGSummary;
}

function StockLevelBar({ percentage, status }: { percentage: number; status: RAGStatus }) {
  const fillClass = {
    GREEN: 'bg-rag-good',
    AMBER: 'bg-rag-warning',
    RED: 'bg-rag-critical',
  }[status];
  const textClass = {
    GREEN: 'text-rag-good',
    AMBER: 'text-rag-warning',
    RED: 'text-rag-critical',
  }[status];

  return (
    <div className="flex min-w-[120px] items-center gap-3">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all duration-300', fillClass)}
          style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
        />
      </div>
      <span className={cn('w-9 shrink-0 text-right font-mono text-xs tabular-nums font-medium', textClass)}>
        {Math.round(percentage)}%
      </span>
    </div>
  );
}

function MachinesTableSkeleton() {
  return (
    <div className="space-y-0">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 border-b border-border px-4 py-4 last:border-0"
        >
          <div className="h-4 w-32 animate-pulse rounded bg-muted" />
          <div className="h-4 w-24 animate-pulse rounded bg-muted" />
          <div className="h-5 w-16 animate-pulse rounded-full bg-muted" />
          <div className="h-1.5 flex-1 animate-pulse rounded-full bg-muted" />
          <div className="flex gap-2">
            <div className="h-8 w-20 animate-pulse rounded-md bg-muted" />
            <div className="h-8 w-16 animate-pulse rounded-md bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

function OnlineIndicator({ isOnline }: { isOnline: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={cn(
          'size-2 rounded-full',
          isOnline ? 'bg-rag-good' : 'bg-rag-offline'
        )}
        aria-hidden
      />
      <span className="text-sm text-foreground">{isOnline ? 'Online' : 'Offline'}</span>
    </span>
  );
}

function MachineConnectivityCell({
  lifecycleStatus,
  isOnline,
}: {
  lifecycleStatus: MachineLifecycleStatus;
  isOnline: boolean;
}) {
  if (lifecycleStatus !== 'LIVE') {
    return <MachineLifecycleBadge status={lifecycleStatus} />;
  }
  return <OnlineIndicator isOnline={isOnline} />;
}

export function MachinesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [machines, setMachines] = useState<MachineData[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [summary, setSummary] = useState<RAGSummary>({ green: 0, amber: 0, red: 0, offline: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [selectedStatus, setSelectedStatus] = useState<RagStatusFilter>(
    parseRagStatusFromParams(searchParams.get('ragStatus'))
  );
  const [selectedLifecycle, setSelectedLifecycle] = useState<LifecycleFilter>(
    parseLifecycleFromParams(searchParams.get('lifecycleStatus'))
  );
  const [page, setPage] = useState(1);

  const liveFleetTotal = summary.green + summary.amber + summary.red + summary.offline;
  const totalPages = Math.max(1, Math.ceil(listTotal / PAGE_SIZE));

  useEffect(() => {
    setSelectedLifecycle(parseLifecycleFromParams(searchParams.get('lifecycleStatus')));
    setSelectedStatus(parseRagStatusFromParams(searchParams.get('ragStatus')));
  }, [searchParams]);

  const qParam = searchParams.get('q') || '';
  useEffect(() => {
    setSearch((prev) => (prev === qParam ? prev : qParam));
    setDebouncedSearch(qParam);
  }, [qParam]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const fetchMachines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (selectedLifecycle) params.set('lifecycleStatus', selectedLifecycle);
      if (selectedStatus) {
        if (selectedStatus === 'OFFLINE') {
          params.set('isOnline', 'false');
          params.set('lifecycleStatus', 'LIVE');
        } else {
          params.set('ragStatus', selectedStatus);
        }
      }
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));

      const response = await apiFetch(`/api/v1/machines?${params}`);

      if (!response.ok) throw new Error('Failed to fetch machines');

      const data: MachineListResponse = await response.json();
      setMachines(data.items);
      setListTotal(data.total);
      setSummary(data.summary);
    } catch {
      setError('Unable to load machines. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, selectedStatus, selectedLifecycle, page]);

  useEffect(() => {
    fetchMachines();
  }, [fetchMachines]);

  const updateSearchParams = useCallback((updates: {
    ragStatus?: RagStatusFilter;
    lifecycleStatus?: LifecycleFilter;
    q?: string;
  }) => {
    const params = new URLSearchParams(searchParams);
    if (updates.ragStatus) {
      params.set('ragStatus', updates.ragStatus);
    } else if (updates.ragStatus === null) {
      params.delete('ragStatus');
      params.delete('isOnline');
    }
    if (updates.lifecycleStatus) {
      params.set('lifecycleStatus', updates.lifecycleStatus);
    } else if (updates.lifecycleStatus === null) {
      params.delete('lifecycleStatus');
    }
    if (updates.q) {
      params.set('q', updates.q);
    } else if (updates.q === '') {
      params.delete('q');
    }
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (debouncedSearch === qParam) return;
    setPage(1);
    updateSearchParams({ q: debouncedSearch });
  }, [debouncedSearch, qParam, updateSearchParams]);

  const handleStockStatusChange = (status: RagStatusFilter) => {
    setSelectedStatus(status);
    setPage(1);
    if (status === 'OFFLINE') {
      setSelectedLifecycle('LIVE');
      updateSearchParams({ ragStatus: status, lifecycleStatus: 'LIVE' });
      return;
    }
    updateSearchParams({ ragStatus: status });
  };

  const handleLifecycleChange = (lifecycle: LifecycleFilter) => {
    setSelectedLifecycle(lifecycle);
    setPage(1);
    updateSearchParams({ lifecycleStatus: lifecycle });
  };

  const clearSearch = () => {
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
    updateSearchParams({ q: '' });
  };

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setSelectedStatus(null);
    setSelectedLifecycle(null);
    setPage(1);
    setSearchParams({});
  };

  const hasActiveFilters = Boolean(search || selectedStatus || selectedLifecycle);

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Machines
            </h1>
            {!loading && listTotal > 0 && (
              <p className="mt-1 text-sm text-muted-foreground">
                {listTotal} total{liveFleetTotal > 0 ? ` · ${liveFleetTotal} live in fleet` : ''}
              </p>
            )}
          </div>
          <Button
            onClick={() => navigate('/machines/new')}
            className="shrink-0 transition-transform duration-150 active:scale-[0.98]"
          >
            <Plus className="size-4" aria-hidden />
            Add machine
          </Button>
        </header>

        <Card className="overflow-hidden border-border/60 shadow-sm">
          <div className="flex flex-col gap-2 border-b border-border/60 px-4 py-3 sm:flex-row sm:items-center">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative flex-1 sm:w-64">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  placeholder="Search name or venue…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className={cn('h-9 pl-9', search && 'pr-9')}
                  aria-label="Search machines"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="size-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>

              <Select
                value={selectedLifecycle ?? 'all'}
                onValueChange={(value) =>
                  handleLifecycleChange(value === 'all' ? null : (value as LifecycleFilter))
                }
              >
                <SelectTrigger className="h-9 w-full sm:w-[160px]" aria-label="Filter by lifecycle">
                  <SelectValue placeholder="Lifecycle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All managed</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                  <SelectItem value="LIVE">Live</SelectItem>
                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                  <SelectItem value="UNMANAGED">Unmanaged</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={selectedStatus ?? 'all'}
                onValueChange={(value) =>
                  handleStockStatusChange(value === 'all' ? null : (value as RagStatusFilter))
                }
              >
                <SelectTrigger className="h-9 w-full sm:w-[180px]" aria-label="Filter by stock status">
                  <SelectValue placeholder="Stock status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stock statuses</SelectItem>
                  <SelectItem value="GREEN">Good</SelectItem>
                  <SelectItem value="AMBER">Low stock</SelectItem>
                  <SelectItem value="RED">Needs refill</SelectItem>
                  <SelectItem value="OFFLINE">Offline (live only)</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 shrink-0"
                  onClick={clearFilters}
                  aria-label="Clear all filters"
                >
                  <FilterX className="size-4" aria-hidden />
                  Clear all
                </Button>
              ) : null}
            </div>
          </div>

          <CardContent className="p-0">
            {error ? (
              <div className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
                <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10">
                  <AlertCircle className="size-6 text-destructive" aria-hidden />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">Could not load machines</p>
                  <p className="text-sm text-muted-foreground">{error}</p>
                </div>
                <Button variant="outline" onClick={fetchMachines}>
                  Try again
                </Button>
              </div>
            ) : loading ? (
              <MachinesTableSkeleton />
            ) : machines.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
                <div className="flex size-12 items-center justify-center rounded-full bg-muted">
                  <Monitor className="size-6 text-muted-foreground" aria-hidden />
                </div>
                <div className="max-w-sm space-y-1">
                  <p className="text-sm font-medium text-foreground">No machines found</p>
                  <p className="text-sm text-pretty text-muted-foreground">
                    {hasActiveFilters
                      ? 'No machines match your current search or filter. Try adjusting or clearing filters.'
                      : 'Add your first machine to start tracking stock levels and connectivity.'}
                  </p>
                </div>
                {hasActiveFilters ? (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear filters
                  </Button>
                ) : (
                  <Button onClick={() => navigate('/machines/new')}>
                    <Plus className="size-4" aria-hidden />
                    Add machine
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-5">Name</TableHead>
                    <TableHead className="hidden md:table-cell">Venue</TableHead>
                    <TableHead className="hidden sm:table-cell">Status</TableHead>
                    <TableHead className="min-w-[120px] md:min-w-[160px]">Stock level</TableHead>
                    <TableHead className="pr-5 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {machines.map((item) => (
                    <TableRow
                      key={item.machine.id}
                      className="group cursor-pointer transition-colors duration-150"
                      onClick={() => navigate(`/machines/${item.machine.id}`)}
                    >
                      <TableCell className="pl-5 font-medium">
                        <TableLink
                          to={machineDetailPath(item.machine.id)}
                          className={entityLinkClass(
                            'inline-flex items-center gap-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                          )}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span
                            className={cn(
                              'size-2 shrink-0 rounded-full sm:hidden',
                              item.machine.lifecycleStatus === 'LIVE'
                                ? item.isOnline
                                  ? 'bg-rag-good'
                                  : 'bg-rag-offline'
                                : 'bg-muted-foreground/40'
                            )}
                            aria-label={
                              item.machine.lifecycleStatus === 'LIVE'
                                ? item.isOnline
                                  ? 'Online'
                                  : 'Offline'
                                : item.machine.lifecycleStatus ?? 'Draft'
                            }
                          />
                          {item.machine.name}
                          <ChevronRight
                            className="size-3.5 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                            aria-hidden
                          />
                        </TableLink>
                      </TableCell>
                      <TableCell className="hidden text-muted-foreground md:table-cell">
                        {item.machine.venue?.name || '—'}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <MachineConnectivityCell
                          lifecycleStatus={item.machine.lifecycleStatus ?? 'LIVE'}
                          isOnline={item.isOnline}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <StockLevelBar
                            percentage={item.ragStatus.percentageFilled}
                            status={item.ragStatus.status}
                          />
                          <RAGBadge
                            status={item.ragStatus.status}
                            percentageFilled={item.ragStatus.percentageFilled}
                            variant="dot"
                            size="sm"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="pr-5 text-right">
                        <div
                          className="flex justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          {item.machine.lifecycleStatus === 'UNMANAGED' ? null : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 gap-1.5 px-3"
                              onClick={() => navigate(`/machines/${item.machine.id}/picklist`)}
                            >
                              <ClipboardList className="size-3.5" aria-hidden />
                              Picklist
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!loading && !error && listTotal > 0 && (
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={listTotal}
                itemLabel="machines"
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MachinesList;
